/*
 * Copyright (C) 2019 Spanning Cloud Apps. LLC  All rights reserved.
 */
import * as moment from 'moment';
import './monthPicker.tpl.html';

angular
  .module('shared.monthPicker', [])
  .directive('sbsfMonthPicker', directive)
  .controller('MonthPickerCtrl', controller);

/**
 * Defines the sbsf-month-picker directive.
 *
 * @returns {Object} - The directive settings
 */
function directive() {
  return {
    controller: 'MonthPickerCtrl',
    templateUrl: 'shared/backupPicker/monthPicker.tpl.html',
    restrict: 'E',
    transclude: true,
    scope: {
      monthMoment: '=',
      minMoment: '=',
      isVisible: '='
    }
  };
}

/**
 * The controller for the directive.
 */
function controller($scope) {
  const DEFAULT_MIN_YEAR = 2010; // Spanning doesn't have any backups older than this, for sure
  const DEFAULT_MIN_MONTH = 0; // January
  const MONTH_NAME_FORMAT = 'MMMM'; // January
  const NOW = moment();

  $scope.getMonthName = getMonthName;
  $scope.hasNextYear = hasNextYear;
  $scope.hasPrevYear = hasPrevYear;
  $scope.isMonthEnabled = isMonthEnabled;
  $scope.selectMonth = selectMonth;
  $scope.selectNextYear = selectNextYear;
  $scope.selectPrevYear = selectPrevYear;

  // In moment, the month numbers are 0-based.
  $scope.monthList = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  $scope.minYear = $scope.minMoment ? $scope.minMoment.year() : DEFAULT_MIN_YEAR;
  $scope.minMonth = $scope.minMoment ? $scope.minMoment.month() : DEFAULT_MIN_MONTH;
  $scope.maxYear = NOW.year();
  $scope.maxMonth = NOW.month();
  $scope.selYear = $scope.monthMoment ? $scope.monthMoment.year() : NOW.year();
  $scope.selMonth = $scope.monthMoment ? $scope.monthMoment.month() : NOW.month();

  watchMinYear();
  watchMinMonth();
  watchSelectedYear();
  watchSelectedMonth();

  /**
   * Returns the name of a month in the picklist.
   *
   * @param {Number} monthNum - The 0-based month number.
   * @returns {String} the month name.
   */
  function getMonthName(monthNum) {
    return moment().month(monthNum).format(MONTH_NAME_FORMAT);
  }

  /**
   * Determines whether we can go forward a year in the month browser.
   *
   * @returns {Boolean} true if we can go forward.
   */
  function hasNextYear() {
    return !$scope.maxYear || $scope.selYear < $scope.maxYear;
  }

  /**
   * Determines whether we can go back a year in the month browser.
   *
   * @returns {Boolean} true if we can go back.
   */
  function hasPrevYear() {
    return !$scope.minYear || $scope.selYear > $scope.minYear;
  }

  /**
   * Determines whether a month should be selectable in the pick list.
   *
   * @param {Number} monthNum - the month to be checked.
   * @returns {Boolean} true if the month is selectable.
   */
  function isMonthEnabled(monthNum) {
    // The selected month is never enabled
    if ($scope.monthMoment) {
      if ($scope.selYear === $scope.monthMoment.year() && $scope.selMonth === monthNum) {
        return false;
      }
    }

    // Months earlier than the min month are disabled
    if ($scope.selYear === $scope.minYear && monthNum < $scope.minMonth) {
      return false;
    }

    // Months greater than the max month are disabled
    if ($scope.selYear === $scope.maxYear && monthNum > $scope.maxMonth) {
      return false;
    }

    return true;
  }

  /**
   * Select the specified month in the current year.
   *
   * @param {Number} monthNum - the number of the month to be selected.
   */
  function selectMonth(monthNum) {
    if ($scope.isMonthEnabled(monthNum)) {
      $scope.monthMoment.set({ year: $scope.selYear, month: monthNum });
      $scope.isVisible = false;
    }
  }

  /**
   * Displays the next year in the month list.
   */
  function selectNextYear() {
    $scope.selYear++;
  }

  /**
   * Displays the previous year in the month list.
   */
  function selectPrevYear() {
    $scope.selYear--;
  }

  /**
   * Watches for changes to the minimum year.
   */
  function watchMinYear() {
    $scope.$watch(
      () => {
        return $scope.minMoment ? $scope.minMoment.year() : DEFAULT_MIN_YEAR;
      },
      function (newVal, oldVal) {
        if (newVal !== oldVal) {
          $scope.minYear = newVal;
        }
      }
    );
  }

  /**
   * Watches for changes to the minimum month.
   */
  function watchMinMonth() {
    $scope.$watch(
      () => {
        return $scope.minMoment ? $scope.minMoment.month() : DEFAULT_MIN_MONTH;
      },
      function (newVal, oldVal) {
        if (newVal !== oldVal) {
          $scope.minMonth = newVal;
        }
      }
    );
  }

  /**
   * Watches for changes to the selected year.
   */
  function watchSelectedYear() {
    $scope.$watch(
      () => {
        return $scope.monthMoment ? $scope.monthMoment.year() : NOW.year();
      },
      function (newVal, oldVal) {
        if (newVal !== oldVal) {
          $scope.selYear = newVal;
        }
      }
    );
  }

  /**
   * Watches for changes to the selected month.
   */
  function watchSelectedMonth() {
    $scope.$watch(
      () => {
        return $scope.monthMoment ? $scope.monthMoment.month() : NOW.month();
      },
      function (newVal, oldVal) {
        if (newVal !== oldVal) {
          $scope.selMonth = newVal;
        }
      }
    );
  }
}
